import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, History } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';
import { Card } from './Card';
import { Skeleton } from './Feedback';
import { ChangeList, type ActivityRow } from './ActivityLog';

/**
 * A module's history, at the foot of its screens.
 *
 * The client asked for the log at the end of every module rather than only on
 * a record: who changed what, across everything in the area, with the old and
 * new value where a field was edited.
 *
 * Collapsed by default (client round 6): available on the page, not a
 * standout. It fetches only once opened, so a module screen does not pay for a
 * history nobody asked to see.
 */
export function ModuleActivity({ module, limit = 20, className }: { module: string; limit?: number; className?: string }) {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    enabled: open,
    queryKey: ['activity', 'module', module, limit],
    queryFn: () => api.get<ActivityRow[]>(`/activity/module/${module}`, { query: { limit } }),
  });

  const rows = query.data ?? [];

  return (
    // `mt-5` here rather than at each call site: the log sits directly under a
    // module's widgets, and on the pages whose container has no vertical rhythm
    // it was flush against the last card (client round 7).
    <Card padded={false} className={cn('mt-5 px-5 py-3', className)}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left text-helper font-semibold text-muted hover:text-ink"
      >
        <History aria-hidden className="h-4 w-4" />
        <span className="flex-1">Activity log</span>
        <span className="font-normal">{open ? 'Hide' : 'Show'}</span>
        <ChevronDown aria-hidden className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
      </button>

      {!open ? null : query.isPending ? (
        <Skeleton className="mt-3 h-40 rounded-lg" />
      ) : rows.length === 0 ? (
        <p className="mt-3 text-helper text-muted">Nothing recorded yet. Changes made from here on will be listed.</p>
      ) : (
        <ol className="mt-3 pb-2">
          {rows.map((row, index) => (
            <li key={row.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                <span aria-hidden className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', row.is_recent ? 'bg-signal' : 'bg-muted/50')} />
                {index < rows.length - 1 && <span aria-hidden className="w-px flex-1 bg-line" />}
              </div>

              <div className={cn('min-w-0 flex-1', index < rows.length - 1 && 'pb-4')}>
                <p className="text-sm text-body">{row.description}</p>
                {(row.changes?.length ?? 0) > 0 && (
                  <ChangeList changes={row.changes!} />
                )}
                <p className="text-helper text-muted">
                  {row.occurred_at ? formatDateTime(row.occurred_at) : '—'}
                  {row.actor ? ` · ${row.actor}` : row.actor_unknown ? ' · recorded before this was tracked' : ''}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
